import React, { useEffect, useRef } from "react";
import { useMediaQuery } from "react-responsive";
import { FaBolt } from "react-icons/fa";
import { PiTimerDuotone } from "react-icons/pi";
import { TbTargetArrow } from "react-icons/tb";
import { useLocation, useNavigate } from "react-router-dom";
import StatBox from "../components/question/StatBox.tsx";
import DotsBackground from "../components/DotsBackground.tsx";
import { completeLessonFromUI } from "../utils/userStorage.ts";
import { useRandomCharacterImage } from "../hooks/useRandomCharacterImage.ts";

type FinishLessonPageProps = {
  xp: number;
  correctCount: number;
  totalCount: number;
  time: number; // seconds
  perfect: boolean;
  lessonId?: string; // Add lessonId prop for lesson completion
  darkMode?: boolean;
};

const getAccuracyTitle = (accuracy: number) => {
  if (accuracy === 100) return "LUAR BIASA";
  if (accuracy >= 80) return "HEBAT";
  if (accuracy >= 50) return "BAGUS";
  return "COBA LAGI";
};

const getTimeTitle = (seconds: number) => {
  if (seconds < 60) return "SANGAT CEPAT";
  if (seconds < 180) return "TEPAT WAKTU";
  return "LEBIH SABAR";
};

const formatTime = (seconds: number) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const FinishLessonPage: React.FC<FinishLessonPageProps> = ({
  xp,
  correctCount,
  totalCount,
  time,
  perfect,
  lessonId,
  darkMode = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const completedRef = useRef(false);
  const characterImg = useRandomCharacterImage();

  const accuracy =
    totalCount === 0
      ? 0
      : Math.min(100, Math.round((correctCount / totalCount) * 100));

  // Handle lesson completion when component mounts
  useEffect(() => {
    // Prevent multiple completions
    if (completedRef.current) {
      console.log("⚠️ Lesson completion already processed, skipping");
      return;
    }

    console.log("🔄 FinishLessonPage useEffect triggered");

    // Try to get lessonId from props, URL params, or location state
    const currentLessonId =
      lessonId ||
      new URLSearchParams(location.search).get("lessonId") ||
      (location.state as any)?.lessonId;

    if (currentLessonId) {
      console.log(
        "🎯 Completing lesson from FinishLessonPage:",
        currentLessonId
      );

      // Mark as completed to prevent duplicate calls
      completedRef.current = true;

      // Complete the lesson and award both money and XP
      const success = completeLessonFromUI(currentLessonId, 10, xp); // 10 money, xp for experience

      if (success) {
        console.log("✅ Lesson completed successfully:", currentLessonId);
        console.log("💰 Money awarded: 10");
        console.log("⭐ XP awarded:", xp);

        // No need for additional event dispatch - completeLessonFromUI already dispatches it
        // The userDataChanged event is already dispatched by saveUserData()
      } else {
        console.error("❌ Failed to complete lesson:", currentLessonId);
        // Reset the flag if completion failed
        completedRef.current = false;
      }
    } else {
      console.warn("⚠️ No lessonId provided to FinishLessonPage");
      console.log("🔍 Debug info:", {
        propsLessonId: lessonId,
        urlParams: new URLSearchParams(location.search).toString(),
        locationState: location.state,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - only run once on mount

  const handleClaimXP = () => {
    // Navigate back to HomePage - the userDataChanged event should already have been dispatched
    // by completeLessonFromUI in the useEffect
    navigate("/learn");
  };

  const isMobile = useMediaQuery({ maxWidth: 767 });
  return (
    <div
      className={`relative min-h-screen flex flex-col items-center justify-center px-2 overflow-x-hidden ${
        darkMode ? "bg-gray-900" : "bg-gray-100"
      }`}
    >
      <DotsBackground darkMode={darkMode} />
      {/* Content */}
      <div className="relative z-10 flex flex-col items-center w-full">
        {/* Character */}
        <img
          src={characterImg}
          alt="Characters"
          className={
            isMobile ? "w-24 h-24 mx-auto mb-3" : "w-48 h-48 mx-auto mb-6"
          }
          draggable={false}
        />

        {/* Main Text */}
        <h1
          className={`${
            isMobile ? "text-xl mb-1" : "text-3xl mb-2"
          } font-bold text-center ${
            darkMode ? "text-gray-100" : "text-gray-800"
          }`}
          style={{ fontFamily: "Feather Bold" }}
        >
          {perfect ? "Pelajaran Sempurna!" : "Pelajaran Selesai!"}
        </h1>
        {/* Subtext */}
        <div
          className={`${
            isMobile ? "text-sm mb-4" : "text-lg mb-8"
          } text-center ${darkMode ? "text-gray-300" : "text-gray-700"}`}
          style={{ fontFamily: "DIN Next Rounded" }}
        >
          {perfect
            ? "Kamu menjawab semua soal dengan benar!"
            : "Bagus! Terus tingkatkan kemampuanmu."}
        </div>

        {/* Stats */}
        <div
          className={`w-full mx-auto ${
            isMobile
              ? "flex flex-row gap-2 mb-5 justify-center"
              : "flex flex-col md:flex-row gap-4 mb-10 justify-center max-w-2xl"
          }`}
        >
          <StatBox color="#ffd600" title="TOTAL XP" darkMode={darkMode}>
            <span
              className={
                isMobile
                  ? "flex items-center text-lg font-bold"
                  : "flex items-center text-2xl font-bold"
              }
            >
              <FaBolt className="mr-2" /> {xp}
            </span>
          </StatBox>
          <StatBox
            color="#43c243"
            title={getAccuracyTitle(accuracy)}
            darkMode={darkMode}
          >
            <span
              className={
                isMobile
                  ? "flex items-center text-lg font-bold"
                  : "flex items-center text-2xl font-bold"
              }
            >
              <TbTargetArrow className="mr-2" /> {accuracy}%
            </span>
          </StatBox>
          <StatBox
            color="#38bdf8"
            title={getTimeTitle(time)}
            darkMode={darkMode}
          >
            <span
              className={
                isMobile
                  ? "flex items-center text-lg font-bold"
                  : "flex items-center text-2xl font-bold"
              }
            >
              <PiTimerDuotone className="mr-2" /> {formatTime(time)}
            </span>
          </StatBox>
        </div>

        {/* Claim Button */}
        <button
          className={`w-full ${
            isMobile ? "max-w-xs py-2 text-base" : "max-w-md py-4 text-lg"
          } rounded-xl shadow transition font-base ${
            darkMode
              ? "bg-blue-700 hover:bg-blue-800 text-white"
              : "bg-blue-500 hover:bg-blue-600 text-white"
          }`}
          onClick={handleClaimXP}
          style={{ fontFamily: "Feather Bold" }}
        >
          AMBIL XP
        </button>
      </div>
    </div>
  );
};

export default FinishLessonPage;
